import { Card, Text, Group, Stack, Avatar, Flex, Box } from "@mantine/core"
import _ from "lodash"

import { Filters } from "@costory/types/filters"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"
import { calculateRelativeChange } from "@costory/shared/utils/format"

import { RechartsAreaChart } from "@costory/front/components/charts/RechartsAreaChart"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useExplorerQuery } from "@costory/front/queries/explore"

type CostCardProps = {
  cost: number
  previousCost: number
  costLabel: string
  previousCostLabel: string
  hideAbsoluteDiff: boolean
  showSparkline?: boolean
  customLabel?: boolean
  filters?: Filters
  currentCostLabel?: string
  cardInDrawer?: boolean
}

export function CostCard({
  cost,
  previousCost,
  costLabel,
  previousCostLabel,
  currentCostLabel,
  filters,
  hideAbsoluteDiff = false,
  showSparkline = false,
  customLabel = false,
  cardInDrawer = false,
}: CostCardProps) {
  const diff = cost - previousCost

  const {
    relativeChange,
    formattedCost,
    formattedPreviousCost,
    formattedDiff,
    color,
    icon,
  } = calculateRelativeChange(diff, previousCost, cost)

  const lastMonthQuery = useExplorerQuery(filters!)

  return (
    <Card withBorder={false} radius="md" p="md" h="100%" bg="surfaceCard">
      <Stack gap="xs">
        <Flex justify="space-between" align="center" w="100%" wrap="wrap">
          {!cardInDrawer && (
            <Text size="md" fw="bold">
              {customLabel
                ? costLabel
                : `${_.startCase(_.toLower(costLabel))} incurred in ${currentCostLabel}`}
            </Text>
          )}
        </Flex>
        <Flex justify="space-between" wrap="wrap" align="flex-end">
          <Text size="xl" fw="bold">
            {formattedCost}
          </Text>
          <Text size="xs" c="dimmed">
            from{" "}
            <Text span size="sm" fw={400} c="dimmed">
              {formattedPreviousCost}
            </Text>{" "}
            in {previousCostLabel}
          </Text>
        </Flex>
        <Flex justify="flex-end" wrap="wrap" gap={4} align="flex-end">
          <Group gap={1}>
            {relativeChange !== "0%" && (
              <Avatar radius="xl" size={18} color={color} variant="light">
                {icon}
              </Avatar>
            )}
            <Text size="md" fw={500} c={color}>
              {hideAbsoluteDiff
                ? relativeChange
                : `${formattedDiff} (${relativeChange})`}
            </Text>
          </Group>
        </Flex>
        {showSparkline && (
          <QueryWrapper query={lastMonthQuery}>
            {({ data }) => {
              const sortedTotalsData = _.orderBy(
                data.totals,
                (item) => dayjs(item.agg_date).toDate(),
                "asc",
              )
              return (
                <Box style={{ flexGrow: 1, minHeight: "60px" }}>
                  <RechartsAreaChart
                    data={sortedTotalsData}
                    color={color}
                    xAxisProps={{
                      ticks: [
                        sortedTotalsData[0]?.agg_date,
                        sortedTotalsData[sortedTotalsData.length - 1]?.agg_date,
                      ],
                      tickFormatter: (value: string) =>
                        dayjs(value).format(DEFAULT_DATE_FORMAT),
                    }}
                  />
                </Box>
              )
            }}
          </QueryWrapper>
        )}
      </Stack>
    </Card>
  )
}
